<template>
  <v-dialog v-model="dialog" max-width="900px" persistent>
    <template v-slot:activator="{ props }">
      <v-btn
        v-bind="props"
        color="#ffb500"
        :prepend-icon="mdiCashPlus"
        variant="elevated"
        class="text-white"
      >
        Ajuster les points
      </v-btn>
    </template>

    <v-card>
      <v-card-title
        class="d-flex align-center pa-4"
        style="background-color: #ffb500"
      >
        <span class="text-h5 text-white">Ajustement de points client</span>
        <v-spacer></v-spacer>
        <v-btn
          :icon="mdiClose"
          variant="text"
          @click="closeDialog"
          color="white"
        ></v-btn>
      </v-card-title>

      <v-card-text class="pt-6">
        <v-form ref="form" v-model="formValid">
          <v-container>
            <!-- Sélection du client -->
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="formData.customerId"
                  :items="customers"
                  item-title="displayName"
                  item-value="id"
                  label="Client *"
                  :prepend-icon="mdiAccount"
                  :rules="[rules.required]"
                  :loading="loadingCustomers"
                  @update:search="debouncedUpdateCustomers"
                  clearable
                  variant="outlined"
                  density="comfortable"
                >
                  <template v-slot:selection="{ item }">
                    {{ item.raw.number }} - {{ item.raw.name }}
                  </template>
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <template v-slot:title>
                        {{ item.raw.number }} - {{ item.raw.name }}
                      </template>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <!-- Montant de l'ajustement -->
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model.number="formData.amount"
                  type="number"
                  label="Montant de l'ajustement *"
                  :prepend-icon="mdiCash"
                  :rules="[rules.required, rules.positiveAmount]"
                  variant="outlined"
                  density="comfortable"
                  hint="Saisissez un montant positif pour ajouter des points"
                  persistent-hint
                  min="1"
                >
                  <template v-slot:append-inner>
                    <v-chip
                      v-if="formData.amount > 0"
                      color="#ffb500"
                      size="small"
                      variant="flat"
                    >
                      Ajout
                    </v-chip>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>

            <!-- Raison de l'ajustement -->
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="formData.reason"
                  label="Raison de l'ajustement *"
                  :prepend-icon="mdiTextBox"
                  :rules="[rules.required]"
                  variant="outlined"
                  rows="3"
                  counter
                  maxlength="500"
                ></v-textarea>
              </v-col>
            </v-row>

            <!-- Champs de répartition -->
            <v-row>
              <v-col cols="12">
                <v-expansion-panels>
                  <v-expansion-panel>
                    <v-expansion-panel-title>
                      <v-icon :icon="mdiChartTree" class="mr-2"></v-icon>
                      Détails de répartition
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-autocomplete
                            v-model="formData.franchiseGroup"
                            :items="franchiseGroups"
                            item-title="name"
                            item-value="name"
                            label="Franchise Group *"
                            :rules="[rules.required]"
                            @update:search="debouncedUpdateFranchiseGroups"
                            variant="outlined"
                            density="comfortable"
                            clearable
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-autocomplete
                            v-model="formData.franchise"
                            :items="franchises"
                            item-title="name"
                            item-value="name"
                            label="Franchise"
                            @update:search="debouncedUpdateFranchises"
                            variant="outlined"
                            density="comfortable"
                            clearable
                          ></v-autocomplete>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-autocomplete
                            v-model="formData.subFranchise"
                            :items="subFranchises"
                            item-title="name"
                            item-value="name"
                            label="Sub franchise"
                            @update:search="debouncedUpdateSubFranchises"
                            variant="outlined"
                            density="comfortable"
                            clearable
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-autocomplete
                            v-model="formData.sapg"
                            :items="sapgs"
                            item-title="name"
                            item-value="name"
                            label="SAPG"
                            @update:search="debouncedUpdateSapgs"
                            variant="outlined"
                            density="comfortable"
                            clearable
                          ></v-autocomplete>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-autocomplete
                            v-model="formData.gimItemType"
                            :items="gimItemTypes"
                            item-title="name"
                            item-value="name"
                            label="GIM Item Type"
                            @update:search="debouncedUpdateGimItemTypes"
                            variant="outlined"
                            density="comfortable"
                            clearable
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-autocomplete
                            v-model="formData.gimBrand"
                            :items="gimBrands"
                            item-title="name"
                            item-value="name"
                            label="GIM Brand"
                            @update:search="debouncedUpdateGimBrands"
                            variant="outlined"
                            density="comfortable"
                            clearable
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-autocomplete
                            v-model="formData.gimSubBrand"
                            :items="gimSubBrands"
                            item-title="name"
                            item-value="name"
                            label="GIM Sub Brand"
                            @update:search="debouncedUpdateGimSubBrands"
                            variant="outlined"
                            density="comfortable"
                            clearable
                          ></v-autocomplete>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-autocomplete
                            v-model="formData.buyingGroupName"
                            :items="buyingGroups"
                            item-title="name"
                            item-value="name"
                            label="Buying Group"
                            @update:search="debouncedUpdateBuyingGroups"
                            variant="outlined"
                            density="comfortable"
                            clearable
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="formData.uom"
                            label="UOM"
                            variant="outlined"
                            density="comfortable"
                            clearable
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="closeDialog">Annuler</v-btn>
        <v-btn
          color="#ffb500"
          variant="elevated"
          @click="validateAdjustment"
          :loading="loading"
          :disabled="!formValid || loading"
        >
          Valider
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Modal de confirmation -->
    <v-dialog v-model="confirmDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title
          class="d-flex align-center pa-4"
          :style="{
            backgroundColor: formData.amount > 0 ? '#ffb500' : '#545857',
          }"
        >
          <span class="text-h6 text-white">Confirmation de l'ajustement</span>
        </v-card-title>

        <v-card-text class="pt-6" v-if="validationResult">
          <v-container>
            <v-alert
              v-if="!validationResult.isValid"
              type="error"
              variant="tonal"
              class="mb-4"
            >
              {{ validationResult.validationMessage }}
            </v-alert>

            <v-list lines="two">
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon :icon="mdiAccount"></v-icon>
                </template>
                <v-list-item-title>Client</v-list-item-title>
                <v-list-item-subtitle>{{
                  validationResult.customerName
                }}</v-list-item-subtitle>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item>
                <template v-slot:prepend>
                  <v-icon :icon="mdiCash"></v-icon>
                </template>
                <v-list-item-title>Solde actuel</v-list-item-title>
                <v-list-item-subtitle>
                  <strong class="text-h6">{{
                    validationResult.currentPoints
                  }}</strong>
                  points
                </v-list-item-subtitle>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item>
                <template v-slot:prepend>
                  <v-icon
                    :icon="formData.amount > 0 ? mdiPlus : mdiMinus"
                    :color="formData.amount > 0 ? '#ffb500' : '#545857'"
                  ></v-icon>
                </template>
                <v-list-item-title>Ajustement</v-list-item-title>
                <v-list-item-subtitle>
                  <strong
                    class="text-h6"
                    :style="{
                      color: formData.amount > 0 ? '#ffb500' : '#545857',
                    }"
                  >
                    {{ formData.amount > 0 ? "+" : "" }}{{ formData.amount }}
                  </strong>
                  points
                </v-list-item-subtitle>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item>
                <template v-slot:prepend>
                  <v-icon :icon="mdiArrowRight"></v-icon>
                </template>
                <v-list-item-title>Nouveau solde</v-list-item-title>
                <v-list-item-subtitle>
                  <strong class="text-h6" style="color: #ffb500">{{
                    validationResult.newPoints
                  }}</strong>
                  points
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>

            <v-alert type="info" variant="tonal" class="mt-4">
              {{ validationResult.validationMessage }}
            </v-alert>
          </v-container>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="cancelConfirmation">Annuler</v-btn>
          <v-btn
            :color="formData.amount > 0 ? '#ffb500' : '#545857'"
            variant="elevated"
            @click="confirmAdjustment"
            :loading="loading"
            :disabled="
              !validationResult || !validationResult.isValid || loading
            "
          >
            Confirmer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import paginations from "@/mixins/paginations";
import _ from "lodash";
import {
  mdiClose,
  mdiAccount,
  mdiCash,
  mdiCashPlus,
  mdiPlus,
  mdiMinus,
  mdiTextBox,
  mdiChartTree,
  mdiArrowRight,
} from "@mdi/js";

export default {
  name: "PointAdjustmentDialog",
  mixins: [paginations],
  data() {
    return {
      // Icons
      mdiClose,
      mdiAccount,
      mdiCash,
      mdiCashPlus,
      mdiPlus,
      mdiMinus,
      mdiTextBox,
      mdiChartTree,
      mdiArrowRight,

      // Dialog states
      dialog: false,
      confirmDialog: false,
      formValid: false,
      loading: false,
      loadingCustomers: false,

      // Form data
      formData: {
        customerId: null,
        amount: 0,
        reason: "",
        franchiseGroup: "",
        franchise: "",
        subFranchise: "",
        sapg: "",
        gimItemType: "",
        gimBrand: "",
        gimSubBrand: "",
        buyingGroupName: "",
        uom: "",
      },

      // Validation rules
      rules: {
        required: (value) => !!value || "Champ obligatoire",
        positiveAmount: (value) =>
          (value && value > 0) || "Le montant doit être positif",
      },

      // Data for autocomplete - initial empty arrays
      // Les vraies données viendront des computed properties
    };
  },
  created() {
    // Créer les méthodes débouncées pour les recherches
    this.debouncedUpdateCustomers = _.debounce(
      (item) => this.updateFields(item, "clients"),
      300
    );
    this.debouncedUpdateFranchiseGroups = _.debounce(
      (item) => this.updateFields(item, "franchiseGroups"),
      300
    );
    this.debouncedUpdateFranchises = _.debounce(
      (item) => this.updateFields(item, "franchises"),
      300
    );
    this.debouncedUpdateSubFranchises = _.debounce(
      (item) => this.updateFields(item, "subFranchises"),
      300
    );
    this.debouncedUpdateSapgs = _.debounce(
      (item) => this.updateFields(item, "sapgs"),
      300
    );
    this.debouncedUpdateGimItemTypes = _.debounce(
      (item) => this.updateFields(item, "gimItemTypes"),
      300
    );
    this.debouncedUpdateGimBrands = _.debounce(
      (item) => this.updateFields(item, "gimBrands"),
      300
    );
    this.debouncedUpdateGimSubBrands = _.debounce(
      (item) => this.updateFields(item, "gimSubBrands"),
      300
    );
    this.debouncedUpdateBuyingGroups = _.debounce(
      (item) => this.updateFields(item, "buyingGroups"),
      300
    );
  },
  computed: {
    ...mapGetters("pointAdjustments", ["validationResult"]),
    // Computed properties qui se mettent à jour automatiquement avec le store
    customers() {
      return this.$store.state.clients.items.map((customer) => ({
        ...customer,
        displayName: `${customer.number} - ${customer.name}`,
      }));
    },
    franchiseGroups() {
      return this.$store.state.franchiseGroups.items;
    },
    franchises() {
      return this.$store.state.franchises.items;
    },
    subFranchises() {
      return this.$store.state.subFranchises.items;
    },
    sapgs() {
      return this.$store.state.sapgs.items;
    },
    gimItemTypes() {
      return this.$store.state.gimItemTypes.items;
    },
    gimBrands() {
      return this.$store.state.gimBrands.items;
    },
    gimSubBrands() {
      return this.$store.state.gimSubBrands.items;
    },
    buyingGroups() {
      return this.$store.state.buyingGroups.items;
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        this.loadFormData();
      } else {
        this.resetForm();
      }
    },
  },
  methods: {
    ...mapActions("pointAdjustments", [
      "validatePointAdjustment",
      "updatePoints",
      "resetState",
    ]),
    ...mapActions("clients", ["fetchAll"]),
    /**
     * Méthode pour gérer les recherches dans les autocomplete
     */
    updateFields(item, search) {
      // Ne lancer la recherche que si on a une vraie valeur de recherche
      if (item && item.length >= 2) {
        this.searchBack(item, 500, search, false);
      }
    },

    /**
     * Charge les données nécessaires pour le formulaire
     */
    async loadFormData() {
      try {
        this.loadingCustomers = true;

        // Charger une liste initiale de clients (les recherches dynamiques affineront)
        await this.$store.dispatch("clients/fetchAll");
        // Le computed customers se met à jour automatiquement

        // Charger les autres référentiels
        await Promise.all([
          this.$store.dispatch("franchiseGroups/fetchAll"),
          this.$store.dispatch("franchises/fetchAll"),
          this.$store.dispatch("subFranchises/fetchAll"),
          this.$store.dispatch("sapgs/fetchAll"),
          this.$store.dispatch("gimItemTypes/fetchAll"),
          this.$store.dispatch("gimBrands/fetchAll"),
          this.$store.dispatch("gimSubBrands/fetchAll"),
          this.$store.dispatch("buyingGroups/fetchAll"),
        ]);
        // Les computed properties se mettent à jour automatiquement
      } catch (error) {
        this.$store.commit("setSnackbar", {
          model: true,
          text: "Erreur lors du chargement des données",
          color: "error",
        });
      } finally {
        this.loadingCustomers = false;
      }
    },

    /**
     * Valide l'ajustement avant confirmation
     */
    async validateAdjustment() {
      const isValid = await this.$refs.form.validate();
      if (!isValid.valid) {
        return;
      }

      this.loading = true;
      try {
        await this.validatePointAdjustment(this.formData);
        this.confirmDialog = true;
      } catch (error) {
        this.$store.commit("setSnackbar", {
          model: true,
          text:
            error.response?.data?.message ||
            "Erreur lors de la validation de l'ajustement",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    /**
     * Confirme et applique l'ajustement
     */
    async confirmAdjustment() {
      this.loading = true;
      try {
        await this.updatePoints(this.formData);

        this.$store.commit("setSnackbar", {
          model: true,
          text: "L'ajustement de points a été appliqué avec succès",
          color: "success",
        });

        this.closeDialog();
        this.$emit("adjustment-completed");
      } catch (error) {
        this.$store.commit("setSnackbar", {
          model: true,
          text:
            error.response?.data?.message ||
            "Erreur lors de l'application de l'ajustement",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    /**
     * Annule la confirmation
     */
    cancelConfirmation() {
      this.confirmDialog = false;
      this.resetState();
    },

    /**
     * Ferme le dialog principal
     */
    closeDialog() {
      this.dialog = false;
      this.confirmDialog = false;
      this.resetForm();
    },

    /**
     * Réinitialise le formulaire
     */
    resetForm() {
      this.$refs.form?.reset();
      this.formData = {
        customerId: null,
        amount: 0,
        reason: "",
        franchiseGroup: "",
        franchise: "",
        subFranchise: "",
        sapg: "",
        gimItemType: "",
        gimBrand: "",
        gimSubBrand: "",
        buyingGroupName: "",
        uom: "",
      };
      // this.resetState();
    },
  },
};
</script>

<style scoped>
.v-expansion-panel-text {
  padding-top: 16px;
}
</style>
